import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { msg } from "@lingui/core/macro";
import * as React from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../../lib/i18n";
import { AIEditBubble } from "./AIEditBubble";
import { type DiffChange, type DiffDecision, reconstructText } from "./InlineDiffPreview";

/**
 * InlineAIRefiner — floating-bubble AI rewriter for any ``<textarea>``
 * (May 26, 2026).
 *
 * SOTA pattern (Notion AI / Linear / Tiptap AI Menu): user highlights a
 * substring inside a free-text field, a bubble appears anchored to the
 * selection with quick actions ("Migliora", "Più formale", "Più breve",
 * "Più lungo") plus a free-form prompt. Submission calls
 * ``POST /actions/{id}/refine-field`` which rewrites ONLY the selection
 * and returns the new full text — the surrounding paragraphs stay
 * verbatim. The replacement feeds back through the standard ``onChange``
 * path so audit captures ``proposed_fields`` vs ``edited_fields``.
 *
 * Render-prop API: the host owns the ``<textarea>`` (so all of the host's
 * styling / value / accessibility stays intact) and threads the refiner
 * handlers through:
 *
 * ```tsx
 * <InlineAIRefiner
 *   proposalId={action.id}
 *   fieldName="body"
 *   value={bodyValue}
 *   onChange={(v) => commit("body", v)}
 *   disabled={disabled}
 * >
 *   {({ textareaRef, onSelect }) => (
 *     <textarea
 *       ref={textareaRef}
 *       value={bodyValue}
 *       onChange={(e) => commit("body", e.target.value)}
 *       onSelect={onSelect}
 *       onMouseUp={onSelect}
 *       onKeyUp={onSelect}
 *     />
 *   )}
 * </InlineAIRefiner>
 * ```
 *
 * The wrapper is intentionally DRY across vendors: any TEXTAREA inside an
 * Action Drawer can opt in by composing this primitive. Email body,
 * SendMessage body, PEC body, Salesforce ``description`` notes, etc. —
 * all light up with the same floating bubble.
 *
 * Anchor strategy: the bubble anchors to the textarea's bounding rect
 * (not the per-character selection rect) — keeps the implementation tiny
 * (no textarea-mirror-div trick) and matches Notion's mobile behaviour
 * where the bubble docks to the editor frame. Floating-UI flips +
 * shifts the bubble so it stays in view on scroll / window edges.
 *
 * ── OPTIONAL diff mode (backward-compatible) ─────────────────────────────────
 * Pass ``submitWithDiff`` to review the rewrite as a per-change inline redline
 * (Apple Writing Tools) BEFORE committing, instead of the default
 * replace-in-place. On submit the host resolves ``{ original, rewritten,
 * changes }``; the bubble then renders {@link InlineDiffPreview} (accept/reject
 * each change) with an "Applica" (orange) / "Annulla" / "Riprova" footer.
 * "Applica" commits ``reconstructText(original, changes, decisions)`` — spliced
 * back into the selection range when the diff covered a selection, else used as
 * the full text. When ``submitWithDiff`` is ABSENT the behaviour is IDENTICAL to
 * today (``submit`` → ``newFullText`` replace-in-place); no existing consumer
 * changes. Hosts wire ``submitWithDiff`` to e.g. ``POST /email/ai-edit`` (native
 * compose whole-body) or a refine-field endpoint that returns ``changes`` (cards).
 *
 * ```tsx
 * <InlineAIRefiner
 *   proposalId={id} fieldName="body" value={body} onChange={setBody}
 *   submitWithDiff={async ({ fullText, selectionStart, selectionEnd, instruction }) =>
 *     aiEditText({ text: fullText.slice(selectionStart, selectionEnd), instruction })}
 * >
 *   {({ textareaRef, onSelect }) => <textarea ref={textareaRef} … />}
 * </InlineAIRefiner>
 * ```
 *
 * ── ⌘K / Ctrl+K entry ────────────────────────────────────────────────────────
 * While the textarea is focused: ⌘K with NO selection opens the bubble over the
 * ENTIRE text (whole-body edit); ⌘K WITH a selection refines that selection (the
 * existing behaviour). Esc closes. Motion respects ``prefers-reduced-motion``.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface InlineRefinerRenderProps {
  /** Attach to the ``<textarea>`` so we can read selectionStart/End. */
  readonly textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Wire to onSelect / onMouseUp / onKeyUp on the textarea. */
  readonly onSelect: React.ReactEventHandler<HTMLTextAreaElement>;
}

export interface InlineAIRefinerProps {
  /** Proposal id — passed through to the BE refine endpoint. */
  readonly proposalId: string;
  /** Logical field name (``body``, ``message``, ``description``, …). */
  readonly fieldName: string;
  /** Current full text of the field. */
  readonly value: string;
  /** Commit a new full text after a successful refine. */
  readonly onChange: (next: string) => void;
  /** When true, the bubble is suppressed (matches the host's disabled state). */
  readonly disabled?: boolean;
  /** Children render-prop receives the handlers to thread onto the textarea. */
  readonly children: (props: InlineRefinerRenderProps) => React.ReactNode;
  /**
   * Submit refine instruction to the BE. ``packages/ui`` deliberately
   * does NOT depend on ``@ando/core`` (it would create a cycle since
   * core imports ui types in a few places), so the host owns the
   * transport. ``apps/client`` ships a thin wrapper that wires this to
   * :func:`@ando/core/api.refineField`.
   *
   * Optional ONLY so a diff-mode host can supply ``submitWithDiff`` alone; at
   * least one transport must be provided. Default (replace-in-place) hosts keep
   * passing ``submit`` exactly as before.
   */
  readonly submit?: (input: RefineSubmitInput) => Promise<RefineSubmitResult>;
  /**
   * OPTIONAL diff-mode transport. When present, a successful submit shows an
   * inline per-change redline in the bubble instead of committing immediately;
   * the user accepts/rejects, then "Applica" commits the reconstructed text.
   * Takes precedence over ``submit`` when both are supplied.
   */
  readonly submitWithDiff?: RefineSubmitWithDiff;
  readonly className?: string;
}

/** Diff-mode transport: resolve the rewrite as a per-change diff script. */
export type RefineSubmitWithDiff = (input: RefineSubmitInput) => Promise<RefineDiffResult>;

export interface RefineDiffResult {
  /** The exact text that was rewritten (selection substring OR whole body). */
  readonly original: string;
  /** All changes applied — the all-accepted baseline. */
  readonly rewritten: string;
  /** Ordered, non-overlapping changes over ``original`` (equal runs omitted). */
  readonly changes: ReadonlyArray<DiffChange>;
}

export interface RefineSubmitInput {
  readonly proposalId: string;
  readonly fieldName: string;
  readonly fullText: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
  readonly instruction: string;
}

export interface RefineSubmitResult {
  readonly success: boolean;
  readonly newFullText?: string | null;
  readonly message?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InlineAIRefiner: React.FC<InlineAIRefinerProps> = ({
  proposalId,
  fieldName,
  value,
  onChange,
  disabled,
  children,
  submit,
  submitWithDiff,
  className,
}) => {
  const { i18n } = useI18n();
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [selectionStart, setSelectionStart] = React.useState(0);
  const [selectionEnd, setSelectionEnd] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Diff mode — set after a successful ``submitWithDiff``; drives the redline.
  const [diff, setDiff] = React.useState<RefineDiffResult | null>(null);
  const [decisions, setDecisions] = React.useState<Record<string, DiffDecision>>({});
  // Last instruction, so "Riprova" can re-run the same rewrite.
  const [lastInstruction, setLastInstruction] = React.useState("");

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    middleware: [offset(8), flip(), shift({ padding: 12 })],
    whileElementsMounted: autoUpdate,
  });

  // Anchor the floating bubble to the textarea once both refs are wired.
  // We can't merge refs the React-ish way because the host owns the
  // textarea ref via the render-prop — instead we mirror it onto the
  // floating-ui reference whenever it changes.
  React.useEffect(() => {
    refs.setReference(textareaRef.current);
  }, [refs]);

  // Single dismiss path — also drops any in-progress diff review so a stale
  // redline never resurfaces on the next open.
  const closeBubble = React.useCallback(() => {
    setOpen(false);
    setDiff(null);
    setDecisions({});
    setError(null);
  }, []);

  // Track the live selection. Fires on mouseup / keyup / selectionchange.
  const onSelect = React.useCallback<React.ReactEventHandler<HTMLTextAreaElement>>(
    (event) => {
      const el = event.currentTarget;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      setSelectionStart(start);
      setSelectionEnd(end);
      if (!disabled && end > start) {
        setOpen(true);
        setError(null);
      } else {
        // Selection collapsed — close the bubble. The free-form input
        // resets too so a stale instruction doesn't fire on the next
        // selection.
        closeBubble();
      }
    },
    [disabled, closeBubble],
  );

  // ⌘K / Ctrl+K entry while the textarea is focused: with a selection → refine
  // it (existing path); with NO selection → open on the WHOLE body.
  React.useEffect(() => {
    if (disabled) return;
    const handler = (ev: KeyboardEvent) => {
      if (!(ev.metaKey || ev.ctrlKey) || ev.key.toLowerCase() !== "k") return;
      const el = textareaRef.current;
      if (!el || document.activeElement !== el) return;
      ev.preventDefault();
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      if (end > start) {
        setSelectionStart(start);
        setSelectionEnd(end);
      } else {
        setSelectionStart(0);
        setSelectionEnd(el.value.length);
      }
      setDiff(null);
      setDecisions({});
      setError(null);
      setOpen(true);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [disabled]);

  // Click-outside dismiss. ``mousedown`` (not click) so we close BEFORE
  // the focusable textarea fires its onSelect with collapsed range.
  React.useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      const target = ev.target as Node | null;
      if (!target) return;
      if (refs.floating.current?.contains(target)) return;
      if (textareaRef.current?.contains(target)) return;
      closeBubble();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, refs.floating, closeBubble]);

  // Escape to dismiss while focused inside the bubble.
  React.useEffect(() => {
    if (!open) return;
    const handler = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        closeBubble();
        textareaRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, closeBubble]);

  const runRefine = React.useCallback(
    async (instruction: string) => {
      if (!instruction.trim()) return;
      if (selectionEnd <= selectionStart) return;
      setBusy(true);
      setError(null);
      setLastInstruction(instruction);
      const submitInput: RefineSubmitInput = {
        proposalId,
        fieldName,
        fullText: value,
        selectionStart,
        selectionEnd,
        instruction,
      };
      try {
        // ── Diff mode — review the rewrite as a redline before committing ──
        if (submitWithDiff) {
          const result = await submitWithDiff(submitInput);
          setDiff(result);
          setDecisions({}); // fresh changes → all pending (previewed as applied)
          return; // keep the bubble open; footer "Applica" commits later
        }
        // ── Default mode — replace-in-place (unchanged behaviour) ──
        if (!submit) return;
        const result = await submit(submitInput);
        if (!result.success || !result.newFullText) {
          setError(result.message ?? i18n._(msg`Impossibile rigenerare la selezione.`));
          return;
        }
        onChange(result.newFullText);
        setOpen(false);
        // Restore the caret to the END of the freshly-rewritten range so
        // the operator can keep typing seamlessly.
        const lenBefore = selectionEnd - selectionStart;
        const lenAfter = result.newFullText.length - (value.length - lenBefore);
        const caret = selectionStart + Math.max(0, lenAfter);
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(caret, caret);
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : i18n._(msg`Errore di rete.`));
      } finally {
        setBusy(false);
      }
    },
    [
      submit,
      submitWithDiff,
      proposalId,
      fieldName,
      value,
      selectionStart,
      selectionEnd,
      onChange,
      i18n,
    ],
  );

  // Commit the diff: splice the reconstructed text back over the reviewed range
  // when the diff covered a selection substring, else use it as the full text.
  const applyDiff = React.useCallback(() => {
    if (!diff) return;
    const reconstructed = reconstructText(diff.original, diff.changes, decisions);
    const coveredSelection = diff.original === value.slice(selectionStart, selectionEnd);
    const next = coveredSelection
      ? value.slice(0, selectionStart) + reconstructed + value.slice(selectionEnd)
      : reconstructed;
    onChange(next);
    closeBubble();
    const caret = coveredSelection ? selectionStart + reconstructed.length : next.length;
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }, [diff, decisions, value, selectionStart, selectionEnd, onChange, closeBubble]);

  const onDecision = React.useCallback((id: string, decision: DiffDecision) => {
    setDecisions((prev) => {
      // Toggle: clicking the active decision again rolls back to pending.
      if (prev[id] === decision) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: decision };
    });
  }, []);

  // The presets-mode hint mirrors the legacy char-count copy so behaviour is
  // unchanged; the diff-mode footer (change count) lives inside AIEditBubble.
  const hint =
    selectionStart === 0 && selectionEnd === value.length
      ? i18n._(msg`Riformula tutto il testo (${value.length} caratteri).`)
      : i18n._(msg`Riformula solo la selezione (${selectionEnd - selectionStart} caratteri).`);

  return (
    <>
      {children({ textareaRef, onSelect })}
      {open && !disabled
        ? createPortal(
            <AIEditBubble
              ref={refs.setFloating}
              style={floatingStyles}
              className={className}
              open
              busy={busy}
              error={error}
              changes={diff?.changes}
              original={diff?.original}
              decisions={decisions}
              onDecision={onDecision}
              onRun={(instruction) => void runRefine(instruction)}
              onApply={applyDiff}
              onCancel={closeBubble}
              onRetry={() => void runRefine(lastInstruction)}
              canRetry={Boolean(lastInstruction)}
              hint={hint}
            />,
            document.body,
          )
        : null}
    </>
  );
};
InlineAIRefiner.displayName = "InlineAIRefiner";
